import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

export default function SignedDocumentsList() {
  const { currentOrg } = useOrg();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const fetchDocs = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await api.getOrgFormInstances(currentOrg.id);
      setDocs((data || []).filter(f => f.status === 'signed'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const inputClass = 'w-full px-3.5 py-[10px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  const filtered = docs.filter(d => {
    if (!query) return true;
    const q = query.toLowerCase();
    const clientName = d.client ? `${d.client.first_name || ''} ${d.client.last_name || ''}`.toLowerCase() : '';
    return clientName.includes(q) || (d.template?.name || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="mb-4">
        <input
          type="text"
          className={inputClass}
          placeholder="Search by patient or document name..."
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-tertiary rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-tertiary rounded-xl border border-theme">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-muted-c mb-3 opacity-40">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <p className="text-[13px] text-muted-c">
            {docs.length === 0 ? 'No signed documents yet' : 'No matches'}
          </p>
          {docs.length === 0 && (
            <p className="text-[11px] text-muted-c mt-1">Signed patient forms will appear here.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => {
            const clientName = d.client ? `${d.client.first_name || ''} ${d.client.last_name || ''}`.trim() : 'Unknown patient';
            return (
              <div key={d.id} className="bg-elevated rounded-xl border border-theme p-4 flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-[12px] bg-success-soft flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-primary-c truncate">
                    {d.template?.name || 'Untitled'}
                  </div>
                  <div className="text-[11px] text-muted-c mt-0.5 truncate">
                    {clientName} &middot; Signed {d.signed_at ? format(new Date(d.signed_at), 'MMM d, yyyy') : '\u2014'}
                  </div>
                </div>
                {d.document_url ? (
                  <a
                    href={d.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3.5 py-2 rounded-[10px] text-[12px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                  >
                    Download
                  </a>
                ) : (
                  <span className="text-[11px] text-muted-c">Preparing...</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
